import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHero from "@/components/feature/PageHero";
import CtaBand from "@/components/feature/CtaBand";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/feature/SectionHeading";
import usePageMeta from "@/hooks/usePageMeta";
import {
  channels,
  dosList,
  dontsList,
  guideTips,
  materialChecklist,
  seoKeywords,
} from "@/mocks/siteData";

const pageTitle = "安全投稿指南 - 如何匿名爆料不暴露身份 | 潜声投稿";
const pageDesc =
  "安全投稿指南教你如何匿名爆料而不暴露身份：用独立设备提交、保留原始视频与录音、上传前先打码、分批提交线索，并附一份可直接照做的素材准备清单与常见误区提醒。";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: pageTitle,
    description: pageDesc,
    inLanguage: "zh-CN",
    url: "https://qiansheng.online/guide",
    about: {
      "@type": "Thing",
      name: "匿名爆料投稿的安全操作方法",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://qiansheng.online/" },
      { "@type": "ListItem", position: 2, name: "安全投稿指南", item: "https://qiansheng.online/guide" },
    ],
  },
];

export default function Guide() {
  usePageMeta({
    title: pageTitle,
    description: pageDesc,
    keywords: seoKeywords,
    path: "/guide",
    jsonLd,
  });

  return (
    <Layout>
      <PageHero
        eyebrow="安全指南"
        title="先学会保护自己，再决定要不要发声"
        subtitle="掌握线索已经需要勇气，把线索交出去更需要方法。这份指南不劝你冲动，只帮你在愿意开口的那一刻，把风险降到更低。"
        image="https://readdy.ai/api/search-image?query=Abstract%20stepped%20pathway%20of%20soft%20light%20squares%20rising%20through%20dark%20teal%20mist%2C%20warm%20amber%20glow%20at%20the%20top%2C%20minimal%20geometric%20editorial%20illustration%2C%20deep%20shadows%2C%20generous%20negative%20space%2C%20high%20contrast%2C%20no%20text%2C%20no%20people&width=1600&height=700&seq=qs-guide-a1&orientation=landscape"
        imageAlt="由发光方块组成的阶梯状路径穿过深青色薄雾，象征安全投稿的步骤指引"
        crumbs={[{ label: "首页", to: "/" }, { label: "安全投稿指南" }]}
      />

      <section className="w-full px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-content mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="八条基本守则"
              title="把风险挡在提交之前"
              desc="这八条是长期处理线索后总结出来的经验，其中任何一条做到位，都能明显降低身份暴露的可能。"
              align="center"
            />
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            {guideTips.map((tip, index) => (
              <Reveal key={tip.id} delay={index * 50}>
                <div className="flex items-start gap-5 py-6 border-b border-background-200">
                  <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 text-xl">
                    <i className={tip.icon}></i>
                  </span>
                  <div>
                    <h3 className="m-0 font-heading text-lg font-semibold text-foreground-950">
                      <span className="text-primary-500 mr-2">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      {tip.title}
                    </h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">{tip.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-16 md:py-20 bg-background-100">
        <div className="max-w-content mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionHeading
                eyebrow="素材准备清单"
                title="投稿前，照着这份清单核对一遍"
                desc="不必全部齐备，但准备好的项目越多，编辑需要向你追问的次数就越少，线索被采纳的概率也越高。"
              />
              <div className="mt-8 p-5 rounded-lg bg-background-50 border border-background-200">
                <p className="flex items-start gap-3 text-sm leading-relaxed text-foreground-600">
                  <i className="ri-lightbulb-flash-line text-accent-600 text-lg mt-0.5"></i>
                  清单只是参考。哪怕只有一条文字描述，也值得先提交，编辑会告诉你还缺什么。
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {materialChecklist.map((item, index) => (
                <Reveal key={item.id} delay={index * 60}>
                  <div className="h-full p-5 rounded-lg bg-background-50 border border-background-200">
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-500 text-background-50 text-sm">
                        <i className="ri-check-line"></i>
                      </span>
                      <span className="font-medium text-sm text-foreground-950">{item.label}</span>
                    </div>
                    <p className="mt-3 text-xs leading-relaxed text-foreground-500">{item.hint}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-content mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="应做与避免"
              title="这些动作，做对了能保命"
              desc="左边是建议坚持做的事，右边是处理线索时最常看到、也最容易出问题的做法。"
              align="center"
            />
          </Reveal>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full p-6 md:p-7 rounded-lg border border-primary-200 bg-primary-50">
                <h3 className="m-0 flex items-center gap-3 font-heading text-xl font-bold text-primary-800">
                  <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className="ri-check-double-line"></i>
                  </span>
                  建议这样做
                </h3>
                <ul className="mt-6 space-y-4">
                  {dosList.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
                      <i className="ri-checkbox-circle-fill text-primary-500 text-lg mt-0.5 shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <div className="h-full p-6 md:p-7 rounded-lg border border-accent-200 bg-accent-50">
                <h3 className="m-0 flex items-center gap-3 font-heading text-xl font-bold text-accent-800">
                  <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                    <i className="ri-close-line"></i>
                  </span>
                  尽量避免
                </h3>
                <ul className="mt-6 space-y-4">
                  {dontsList.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
                      <i className="ri-close-circle-fill text-accent-500 text-lg mt-0.5 shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pb-16 md:pb-24">
        <div className="max-w-content mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="通道选择建议"
              title="看完指南，该走哪条通道了？"
            />
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {channels.map((channel, index) => (
              <Reveal key={channel.id} delay={index * 60}>
                <Link
                  to={`/channels#${channel.slug}`}
                  className="group flex flex-col h-full p-6 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700 text-xl">
                    <i className={channel.icon}></i>
                  </span>
                  <h3 className="mt-5 font-heading text-base font-bold text-foreground-950 group-hover:text-primary-700 transition-colors">
                    {channel.name}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-foreground-600 flex-1">
                    {channel.short}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600">
                    前往通道
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        title="准备好了，就开始吧"
        desc="按清单把最关键的两三项材料整理好，选一条通道提交。剩下的判断，交给编辑和你一起完成。"
        primaryLabel="前往投稿通道"
        primaryTo="/channels"
        secondaryLabel="仍有疑问，看常见问题"
        secondaryTo="/faq"
      />
    </Layout>
  );
}